/** Upgrade offers calculated by the owner (Stargate catalog). The Nave only presents them. */
export type CustomerUpgradeOffer = Readonly<{
  offerId: string;
  serviceName: string;
  logoUrl?: string;
  cadence: "monthly" | "yearly" | "other";
  currency: string;
  current: Readonly<{ label: string; amount: string }>;
  target: Readonly<{ label: string; amount: string }>;
  differenceAmount: string;
  /** What the target plan includes per the Stargate catalog; `isNew` = not in the current plan. */
  includes?: Readonly<{ comparedWithCurrent: boolean; items: readonly Readonly<{ label: string; isNew: boolean }>[] }>;
  /** Owner-computed charge for switching today: the difference for the days left in the cycle. */
  proration?: Readonly<{ cycleStart: string; nextCycleDate: string; totalDays: number; remainingDays: number; amountDueToday: string }>;
}>;

const record = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value: unknown, max: number): string | undefined => typeof value === "string" && value.trim() !== "" && value.length <= max ? value.trim() : undefined;
const decimal = (value: unknown): string | undefined => typeof value === "string" && /^\d{1,12}\.\d{2}$/u.test(value) ? value : undefined;
const UUID_PAIR = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

const telescupLogo = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "cdn.telescup.com.br" && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
};

/** Optional feature list: a malformed list is dropped (the offer itself stays valid). */
const projectIncludes = (input: unknown): CustomerUpgradeOffer["includes"] => {
  if (!record(input) || typeof input.comparedWithCurrent !== "boolean" || !Array.isArray(input.items) || input.items.length === 0 || input.items.length > 20) return undefined;
  const items = input.items.map((item) => record(item) && typeof item.isNew === "boolean" ? { label: text(item.label, 160), isNew: item.isNew } : undefined);
  if (!items.every((item): item is { label: string; isNew: boolean } => item !== undefined && item.label !== undefined)) return undefined;
  return { comparedWithCurrent: input.comparedWithCurrent, items };
};

const isoDate = (value: unknown): string | undefined => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/u.test(value) ? value : undefined;
const days = (value: unknown): number | undefined => typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 366 ? value : undefined;

/** Optional pro-rata quote: dropped (offer kept) unless every field is well formed and consistent. */
const projectProration = (input: unknown): CustomerUpgradeOffer["proration"] => {
  if (!record(input)) return undefined;
  const cycleStart = isoDate(input.cycleStart), nextCycleDate = isoDate(input.nextCycleDate);
  const totalDays = days(input.totalDays), remainingDays = days(input.remainingDays), amountDueToday = decimal(input.amountDueToday);
  if (!cycleStart || !nextCycleDate || !totalDays || !remainingDays || !amountDueToday || remainingDays > totalDays || cycleStart >= nextCycleDate) return undefined;
  return { cycleStart, nextCycleDate, totalDays, remainingDays, amountDueToday };
};

const projectOffer = (input: unknown): CustomerUpgradeOffer | undefined => {
  if (!record(input) || !record(input.current) || !record(input.target)) return undefined;
  const offerId = typeof input.offerId === "string" && UUID_PAIR.test(input.offerId) ? input.offerId : undefined;
  const serviceName = text(input.serviceName, 160);
  const currentLabel = text(input.current.label, 60), targetLabel = text(input.target.label, 60);
  const currentAmount = decimal(input.current.amount), targetAmount = decimal(input.target.amount), differenceAmount = decimal(input.differenceAmount);
  const currency = typeof input.currency === "string" && /^[A-Z]{3}$/u.test(input.currency) ? input.currency : undefined;
  if (!offerId || !serviceName || !currentLabel || !targetLabel || !currentAmount || !targetAmount || !differenceAmount || !currency) return undefined;
  const logoUrl = telescupLogo(input.logoUrl);
  const includes = projectIncludes(input.includes);
  const proration = projectProration(input.proration);
  return {
    offerId, serviceName, ...(logoUrl ? { logoUrl } : {}), currency,
    cadence: input.cadence === "monthly" || input.cadence === "yearly" ? input.cadence : "other",
    current: { label: currentLabel, amount: currentAmount },
    target: { label: targetLabel, amount: targetAmount },
    differenceAmount,
    ...(includes ? { includes } : {}),
    ...(proration ? { proration } : {}),
  };
};

/** Allow-list: anything not recognised is dropped; a malformed offer invalidates the response. */
export const offersProjection = (input: unknown): Readonly<{ offers: readonly CustomerUpgradeOffer[] }> | undefined => {
  if (!record(input) || !Array.isArray(input.offers) || input.offers.length > 20) return undefined;
  const offers = input.offers.map(projectOffer);
  return offers.every((offer): offer is CustomerUpgradeOffer => offer !== undefined) ? { offers } : undefined;
};
